import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppBar, Box, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import { ReactComponent as Nine_dots } from "../../../assets/icons/Nine_dots.svg";
import { ReactComponent as Menubar } from "../../../assets/icons/menu_bar.svg";
import { useGlobalContext } from "../../../context/GlobalContext";
import RightSideBarModal from "../../ui/modals/RightSideBarModal";
import { ReactComponent as Home1 } from "../../../assets/navIcons/home.svg";
import { ReactComponent as Home2 } from "../../../assets/navIcons/D_home.svg";
import { ReactComponent as Cart1 } from "../../../assets/navIcons/cart.svg";
import { ReactComponent as Cart2 } from "../../../assets/navIcons/D_cart.svg";
import { ReactComponent as Order1 } from "../../../assets/navIcons/order.svg";
import { ReactComponent as Order2 } from "../../../assets/navIcons/D_order.svg";
import { ReactComponent as NavLogo } from "../../../assets/navIcons/NavbarLogo.svg";
import "./navbar.css";
import CustomTooltip from "../../ui/CustomTooltip";
import { getIcon } from "../../../utils/iconConfig";
import SearchModal from "../../ui/modals/searchModal/SearchModal";
import * as XLSX from "xlsx";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeItem, setActiveItem] = useState("");
  const [logoutModal, setLogoutModal] = useState(false);
  const [searchModal, setSearchModal] = useState(false);
  const [roleSwitched, setRoleSwitched] = useState(
    () => localStorage.getItem("roleSwitched") === "true"
  );

  const [rights, setRights] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rights")) || {}; } catch { return {}; }
  });

  // Re-read rights after MainLayout fetches + stores them
  useEffect(() => {
    const sync = () => {
      try { setRights(JSON.parse(localStorage.getItem("rights")) || {}); } catch {}
    };
    window.addEventListener("storage", sync);
    window.addEventListener("rights-updated", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("rights-updated", sync);
    };
  }, []);

  const hasRoleSwitch = !!rights["Role Switch"]?.view;

  const data = JSON.parse(localStorage.getItem("ClientData"));
  let clientName =
    data?.name === "Binnys Jewellery Pvt. Ltd. (Client not Found)"
      ? data?.newClientName
      : data?.name;

  const {
    setHomeScreen,
    setCartScreen,
    rightSideBar,
    setRightSideBar,
    setClientForm,
    setScanned,
    clientForm,                
  } = useGlobalContext();

  const handleItemClick = (item) => {
    setActiveItem(item);
  };

  // Report is a super admin screen, not a Select one - gated on the real
  // role's isAdmin flag, separate from the Select-local "Admin"/"Stock
  // Manager" role the other tabs answer to.
  const isSuperAdmin = localStorage.getItem("isAdmin") === "true";

  const iconData = [
    {
      name: "Home",
      pathName: "home",
      enableicon: <Home1 />,
      disableicon: <Home2 />,
    },
    {
      name: "Cart",
      pathName: "cart",
      enableicon: <Cart1 />,
      disableicon: <Cart2 />,
    },
    {
      name: "Order",
      pathName: "order",
      enableicon: <Order1 />,
      disableicon: <Order2 />,
    },
    ...(isSuperAdmin
      ? [
          {
            // No dedicated active/inactive icon pair exists for Report yet,
            // unlike the other tabs - the same icon is reused for both
            // states, told apart by opacity instead of a second SVG.
            name: "Report",
            pathName: "report",
            enableicon: (
              <span className="report-nav-icon report-nav-icon-active">
                {getIcon("Score")}
              </span>
            ),
            disableicon: (
              <span className="report-nav-icon">{getIcon("Score")}</span>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    const path = location?.pathname.split("/")[1];
    switch (path) {
      case "home":
        setActiveItem("Home");
        break;
      case "cart":
        setActiveItem("Cart");
        break;
      case "order":
        setActiveItem("Order");
        break;
      case "report":
        setActiveItem("Report");
        break;
      default:
        setActiveItem("");
    }
  }, [location?.pathname]);

  function handlelogout() {
    navigate("/login");
    setRightSideBar(false);
  }

  const handleRoleSwitch = (checked) => {
    if (checked) {
      // Store original role before switching
      const currentRole = localStorage.getItem("role");
      if (currentRole !== "Stock Manager") {
        localStorage.setItem("originalRole", currentRole);
      }
      localStorage.setItem("role", "Stock Manager");
      localStorage.setItem("roleSwitched", "true");
      setRoleSwitched(true);
      navigate("/stock");
    } else {
      const originalRole = localStorage.getItem("originalRole") || "Admin";
      localStorage.setItem("role", originalRole);
      localStorage.removeItem("roleSwitched");
      localStorage.removeItem("originalRole");
      setRoleSwitched(false);
      navigate("/home");
    }
  };

  const modalBtnInfo = useMemo(
    () => [
      {
        btnName: "log out",
        bgColor: "#E77B7B",
        click: handlelogout,
      },
      {
        btnName: "Keep",
        bgColor: "#50C878",
        click: () => setLogoutModal(false),
      },
    ],
    []
  );

  const handleClear = () => {
    let clientData = JSON.parse(localStorage.getItem("ClientData"));
    if (!clientData) return;
    const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
    if (cartItems?.length > 0) {
      const designs = cartItems.flatMap((item) => {
        return (
          item?.quantityData?.map((des) => {
            const { key, ...quantityFields } = des || {};

            const cleanedFields = Object.fromEntries(
              Object.entries(quantityFields).map(([field, value]) => [
                field,
                value === "" ? null : value,
              ])
            );

            return {
              id: item?._id,
              styleNumber: item?.styleNumber,
              diaQlty: cleanedFields?.diaQty || null,
              itemSize: cleanedFields?.itemSize || null,
              goldColor: cleanedFields?.goldColor || null,
              goldQlty: cleanedFields?.goldQty || null,
              message: cleanedFields?.message || null,
            };
          }) || []
        );
      });

      const orderpayload = {
        clientName: clientData?.name || clientData?.newClientName,
        designs,
      };

      const now = new Date();

      const day = String(now.getDate()).padStart(2, "0");
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const year = now.getFullYear();
      const dateStr = `${day}-${month}-${year}`;

      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const timeStr = `${hours}-${minutes}-${seconds}`;

      const worksheet = XLSX.utils.json_to_sheet(
        orderpayload?.designs.map(({ id, ...rest }) => rest)
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Designs");

      const fileName = `${orderpayload?.clientName}_${dateStr}_${timeStr}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    }
    // navigate("/home");
    // localStorage.removeItem("scanned");
    // localStorage.removeItem("cartItems");
    // localStorage.removeItem("fromHomeData");
    // localStorage.removeItem("ClientData");
    // localStorage.removeItem("clientOrderData");
    // setHomeScreen(false);
    // setCartScreen(false);
    // setClientForm(false);
    // setScanned(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "#000",
          boxShadow: "0vw .2vw .5vw #0D0D0D",
          height: "10vh",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            columnGap: "2vw",
            height: "100%",
            margin: "0 4vw",
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifySelf: "start",
            }}
          >
            <NavLogo
              style={{ cursor: "pointer" }}
              onClick={() => handleClear()}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              className="nav-links"
              sx={{
                visibility:
                  location.pathname === "/stock" ||
                  location.pathname === "/Stock"
                    ? "hidden"
                    : "visible",
              }}
            >
              {iconData?.map((data, index) => {
                return (
                  <Link
                    to={"/" + data.pathName}
                    className="sidebar-custom-link"
                    key={index}
                  >
                    <div
                      className="custom-list-item"
                      onClick={() => handleItemClick(data.name)}
                    >
                      {activeItem === data.name
                        ? data.enableicon
                        : data.disableicon}

                      <span
                        className={`sidebar-icon-name ${
                          activeItem === data.name
                            ? "sidebar-icon-name-active"
                            : ""
                        }`}
                      >
                        {data.name}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              justifySelf: "end",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {clientForm &&
              (location?.pathname === "/home" ||
                location?.pathname === "/cart") && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1vw",
                    minWidth: 0,
                    marginRight: ".8vw",
                  }}
                >
                  {location?.pathname === "/home" && (
                    <span
                      style={{
                        display: "flex",
                        cursor: "pointer",
                      }}
                      onClick={() => setSearchModal(true)}
                    >
                      {getIcon("Search")}
                    </span>
                  )}

                  <Tooltip
                    title={clientName}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "white",
                          color: "#101010",
                          fontFamily: "Karla",
                          fontSize: "0.8vw",
                          fontWeight: 500,
                          borderRadius: ".25vw",
                          padding: ".4vw .6vw",
                        },
                      },
                      arrow: { sx: { color: "white" } },
                    }}
                  >
                    <Typography
                      noWrap
                      style={{
                        fontSize: "1vw",
                        fontFamily: "Poppins",
                        minWidth: 0,
                      }}
                    >
                      {location?.pathname === "/home"
                        ? `Hi, ${clientName}`
                        : `${clientName}'s Cart`}
                    </Typography>
                  </Tooltip>
                </div>
              )}
            {hasRoleSwitch && (
              <Box
                onClick={() => handleRoleSwitch(!roleSwitched)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".5vw",
                  marginRight: ".6vw",
                  background: roleSwitched ? "#50C87822" : "#0D0D0D",
                  borderRadius: ".4vw",
                  padding: ".35vw .7vw",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                }}
              >
                <Box
                  sx={{
                    width: ".6vw",
                    height: ".6vw",
                    borderRadius: "50%",
                    background: roleSwitched ? "#50C878" : "#555",
                    transition: "background 0.2s ease",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  fontFamily="Karla"
                  fontSize=".72vw"
                  fontWeight={500}
                  color={roleSwitched ? "#50C878" : "#888"}
                  whiteSpace="nowrap"
                  lineHeight={1}
                  sx={{ transition: "color 0.2s ease" }}
                >
                  Stock View
                </Typography>
                <Switch
                  size="small"
                  checked={roleSwitched}
                  onChange={(e) => { e.stopPropagation(); handleRoleSwitch(e.target.checked); }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    padding: 0,
                    width: "2.2vw",
                    height: "1.1vw",
                    "& .MuiSwitch-switchBase": {
                      padding: ".15vw",
                      "&.Mui-checked": {
                        color: "#50C878",
                        transform: "translateX(1.1vw)",
                      },
                      "&.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#50C87866",
                        opacity: 1,
                      },
                    },
                    "& .MuiSwitch-thumb": {
                      width: ".8vw",
                      height: ".8vw",
                    },
                    "& .MuiSwitch-track": {
                      borderRadius: "1vw",
                      backgroundColor: "#2A2A2A",
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            )}
            <CustomTooltip title="Menu">
              <IconButton
                size="large"
                edge="end"
                color="success"
                onClick={() => setRightSideBar(true)}
                disableRipple
                disableFocusRipple
              >
                {rightSideBar ? <Menubar /> : <Nine_dots />}
              </IconButton>
            </CustomTooltip>
          </Box>
        </div>
      </AppBar>
      <RightSideBarModal
        open={rightSideBar}
        handleClose={() => setRightSideBar(false)}
        modalBtnInfo={modalBtnInfo}
        logoutModal={logoutModal}
        setLogoutModal={setLogoutModal}
      />
      <SearchModal
        headLabel="Search"
        openModal={searchModal}
        handleClose={() => setSearchModal(false)}
      />
    </>
  );
};

export default Navbar;

